'use client'

/**
 * FeedsBoard — the live, sortable board for /feeds. SSR hands it the first page of rows
 * (SEO + instant paint); it then auto-refreshes from /api/feeds and lets the user re-sort
 * between Most Active / Top Movers / Most Discussed without a refetch.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Link from 'next/link'
import { Flame, MessageCircle, Radio, ArrowUpRight, TrendingUp, TrendingDown, MessageSquare, Zap } from 'lucide-react'
import { runnerRecency } from '@/lib/dashboard/runnerRecency'
import type { FeedRow } from '@/lib/feeds'
import { isLeveragedEtf, LEV_TOOLTIP } from '@/lib/useLeveragedEtfFilter'
import { UniverseChips } from '@/components/UniverseChips'
import { whenVisible } from '@/lib/whenVisible'
import ListFilters from '@/components/ListFilters'
import { useUniverseFilters } from '@/lib/filters/useUniverseFilters'
import { useSymbolMeta } from '@/lib/filters/useSymbolMeta'
import { passesUniverse } from '@/lib/filters/universeFilter'

const isLevEtf = (r: FeedRow): boolean => isLeveragedEtf(r.symbol, r.name)

type FeedWin = 'day' | 'recent'
type Tab = 'active' | 'movers' | 'discussed'
const TABS: { id: Tab; label: string; icon: typeof Flame; hint: string }[] = [
  { id: 'active', label: 'Most Active', icon: Flame, hint: 'Ranked by alerts in the last 24h' },
  { id: 'movers', label: 'Top Movers', icon: TrendingUp, hint: 'Ranked by absolute day % move' },
  { id: 'discussed', label: 'Most Discussed', icon: MessageSquare, hint: 'Ranked by community posts' },
]

const fmtPrice = (p: number) => (p < 1 ? p.toFixed(3) : p.toFixed(2))
const dayPct = (r: FeedRow): number | null =>
  r.price != null && r.prevClose != null && r.prevClose > 0 ? ((r.price - r.prevClose) / r.prevClose) * 100 : null

function ago(ms: number | null, now: number): string {
  if (!ms) return ''
  const s = Math.max(1, Math.floor((now - ms) / 1000))
  if (s < 60) return `${s}s ago`
  const m = Math.floor(s / 60); if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`
  return `${Math.floor(h / 24)}d ago`
}

/** Compact intraday sparkline. Green if the series closed above where it opened, else red. */
function Spark({ data, w = 84, h = 30 }: { data: number[]; w?: number; h?: number }) {
  if (!data || data.length < 2) return <div style={{ width: w, height: h }} className="rounded bg-zinc-800/30" />
  const min = Math.min(...data), max = Math.max(...data)
  const span = max - min || 1
  const up = data[data.length - 1] >= data[0]
  const stroke = up ? '#34d399' : '#fb7185'
  const fill = up ? 'rgba(52,211,153,0.12)' : 'rgba(251,113,133,0.12)'
  const pad = 2
  const pts = data.map((v, i) => {
    const x = pad + (i / (data.length - 1)) * (w - pad * 2)
    const y = pad + (1 - (v - min) / span) * (h - pad * 2)
    return [x, y] as const
  })
  const line = pts.map((p) => `${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ')
  const area = `${pts[0][0].toFixed(1)},${(h - pad).toFixed(1)} ${line} ${pts[pts.length - 1][0].toFixed(1)},${(h - pad).toFixed(1)}`
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="overflow-visible" aria-hidden>
      <polygon points={area} fill={fill} />
      <polyline points={line} fill="none" stroke={stroke} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={pts[pts.length - 1][0]} cy={pts[pts.length - 1][1]} r={2} fill={stroke} />
    </svg>
  )
}

/**
 * ⚠ THE `logo` PROP IS ALWAYS NULL AND ALWAYS WAS (measured 2026-08-09). feeds.ts runs the same
 * cleanLogo() as eight sibling data layers, which nulls every cdn.polygon.io URL because that host is
 * dead — and every row in stocks.logo_url is one. So this component has only ever rendered the
 * monogram; no user has seen a real logo here.
 * ⭐ The fix is the proxy the rest of the site already uses: /api/logo?symbol= (Redis-cached, rate
 * limited, with fallback sources). FiftyTwoWeekBoard, TodayMoversBoard, GapScannerBoard, SimilarStocks
 * and MoneyTreemap have all been on it; this component and NextSessionPlaybook were the stragglers.
 * `logo` is kept only as a "has something on file" hint, and `ok` still drives the monogram fallback.
 */
function Logo({ symbol, logo }: { symbol: string; logo: string | null }) {
  const [ok, setOk] = useState(true)
  void logo
  if (ok) {
    // eslint-disable-next-line @next/next/no-img-element
    return <img src={`/api/logo?symbol=${encodeURIComponent(symbol)}`} alt="" loading="lazy" onError={() => setOk(false)} className="h-9 w-9 shrink-0 rounded-full bg-white object-contain p-0.5 ring-1 ring-zinc-700" />
  }
  return (
    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-amber-500/25 to-amber-700/25 text-xs font-bold text-amber-300 ring-1 ring-amber-600/30">
      {symbol.slice(0, 2)}
    </div>
  )
}

function rankBadge(i: number): { cls: string; label: string } {
  if (i === 0) return { cls: 'bg-amber-400/20 text-amber-300 ring-amber-400/40', label: '1' }
  if (i === 1) return { cls: 'bg-zinc-300/15 text-zinc-200 ring-zinc-300/30', label: '2' }
  if (i === 2) return { cls: 'bg-orange-500/15 text-orange-300 ring-orange-500/30', label: '3' }
  return { cls: 'text-zinc-600', label: String(i + 1) }
}

export default function FeedsBoard({ initial, initialAt }: { initial: FeedRow[]; initialAt: number }) {
  const [items, setItems] = useState<FeedRow[]>(initial)
  const [updatedAt, setUpdatedAt] = useState<number>(initialAt)
  const [tab, setTab] = useState<Tab>('active')
  const [win, setWin] = useState<FeedWin>('day')   // 'day' = whole session, 'recent' = last 2h
  const [now, setNow] = useState<number>(initialAt)

  // Live, split-adjusted quotes. Our DB price/prevClose can be on different split scales for a stock that
  // recently reverse-split (e.g. $KORU showed -95% from a stale pre-split minute price vs a post-split
  // prevClose). The live quote is authoritative and split-safe, so we prefer it for BOTH the displayed %
  // and the Top Movers sort; the raw DB % only fills in when there's no live quote (and absurd values are
  // dropped below).
  const [live, setLive] = useState<Record<string, { price: number; chgPct: number }>>({})
  const symbolsKey = useMemo(() => items.map((r) => r.symbol.toUpperCase()).slice(0, 60).join(','), [items])
  useEffect(() => {
    if (!symbolsKey) return
    let cancelled = false
    const load = async () => {
      try {
        const r = await fetch(`/api/watchlist/quotes?symbols=${encodeURIComponent(symbolsKey)}`, { cache: 'no-store' })
        if (!r.ok) return
        const j = await r.json()
        if (cancelled || !Array.isArray(j.quotes)) return
        const m: Record<string, { price: number; chgPct: number }> = {}
        for (const q of j.quotes) if (q?.symbol && q.chgPct != null && Number.isFinite(Number(q.chgPct))) m[String(q.symbol).toUpperCase()] = { price: Number(q.price), chgPct: Number(q.chgPct) }
        setLive((prev) => ({ ...prev, ...m }))
      } catch { /* keep last good */ }
    }
    load()
    const iv = setInterval(whenVisible(load), 25_000)
    return () => { cancelled = true; clearInterval(iv) }
  }, [symbolsKey])

  // Prefer the live split-adjusted quote; fall back to the DB % but drop absurd sub-(-90)% values that are
  // almost always a stale pre-reverse-split price, not a real move.
  const pctOf = useCallback((r: FeedRow): number | null => {
    const lv = live[r.symbol.toUpperCase()]?.chgPct
    if (lv != null && Number.isFinite(lv)) return lv
    const db = dayPct(r)
    return db != null && db > -90 ? db : null
  }, [live])
  const priceOf = useCallback((r: FeedRow): number | null => live[r.symbol.toUpperCase()]?.price ?? r.price, [live])

  const refresh = useCallback(async (window: FeedWin) => {
    try {
      const r = await fetch(`/api/feeds?window=${window}`, { cache: 'no-store' })
      if (!r.ok) return
      const j = await r.json()
      // 'recent' can legitimately be empty off-hours (nothing alerted in 2h) — still apply it so the
      // board reflects the chosen window; 'day' keeps the last good set if a refresh returns nothing.
      if (Array.isArray(j.items) && (window === 'recent' || j.items.length)) {
        setItems(j.items); setUpdatedAt(j.generatedAt || Date.now())
      }
    } catch { /* keep last good */ }
  }, [])

  // Refetch immediately when the window changes (skip the very first 'day' render — SSR already has it).
  const firstWin = useRef(true)
  useEffect(() => {
    if (firstWin.current && win === 'day') { firstWin.current = false; return }
    firstWin.current = false
    refresh(win)
  }, [win, refresh])

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 15_000)
    const p = setInterval(whenVisible(() => refresh(win)), 45_000)
    setNow(Date.now())
    return () => { clearInterval(t); clearInterval(p) }
  }, [refresh, win])

  // Shared universe filter (market cap / liquidity / OTC / type / country / sector), applied client-side
  // on the fetched pool BEFORE the top-N slice so the board stays full. Defaults to the "hide the junk"
  // state for everyone; a signed-in user's saved settings load over it.
  const { filters, setFilters } = useUniverseFilters()
  // Leveraged/inverse ETFs: the universe filter's "leveraged ETFs" exclusion is the single source of truth
  // now (the old standalone toggle was redundant). Name-based hide (no meta needed) mirrors that setting so
  // it applies instantly; passesUniverse also covers it via meta.
  const hideLev = filters.excludeTypes.includes('leveragedEtf')
  const feedSymbols = useMemo(() => items.map((r) => r.symbol), [items])
  const { meta: uMeta } = useSymbolMeta(feedSymbols)
  const passesU = useCallback(
    (r: FeedRow) => {
      const m = uMeta.get(r.symbol.toUpperCase())
      return !m || passesUniverse(m, filters)
    },
    [uMeta, filters],
  )
  const hiddenByFilter = useMemo(() => items.filter((r) => (!hideLev || !isLevEtf(r)) && !passesU(r)).length, [items, hideLev, passesU])

  const sorted = useMemo(() => {
    const arr = items.filter((r) => (!hideLev || !isLevEtf(r)) && passesU(r))
    if (tab === 'movers') arr.sort((a, b) => Math.abs(pctOf(b) ?? 0) - Math.abs(pctOf(a) ?? 0))
    else if (tab === 'discussed') arr.sort((a, b) => b.posts - a.posts || b.alerts - a.alerts)
    else arr.sort((a, b) => b.alerts - a.alerts)
    // Pool is now ~60 (see feeds.ts) so Top Movers / Most Discussed can rank across more names; each
    // tab still displays a focused top 30.
    return arr.slice(0, 30)
  }, [items, tab, hideLev, pctOf, passesU])

  const totals = useMemo(() => ({
    tickers: items.length,
    alerts: items.reduce((s, r) => s + r.alerts, 0),
    posts: items.reduce((s, r) => s + r.posts, 0),
  }), [items])

  // A ticker is "hot" if it fired an alert in the last 10 min or sits in the top ~alert tier.
  const hotFloor = useMemo(() => {
    const top = [...items].sort((a, b) => b.alerts - a.alerts)[Math.min(2, items.length - 1)]
    return top ? top.alerts : Infinity
  }, [items])
  const isHot = (r: FeedRow) => (r.lastAlertMs != null && now - r.lastAlertMs < 10 * 60_000) || r.alerts >= hotFloor

  return (
    <div>
      {/* Summary chips */}
      <div className="mb-5 flex flex-wrap items-center gap-2.5">
        {[
          { k: 'Active tickers', v: totals.tickers, c: 'text-zinc-200' },
          { k: '24h alerts', v: totals.alerts.toLocaleString(), c: 'text-amber-300' },
          { k: 'Posts', v: totals.posts.toLocaleString(), c: 'text-sky-300' },
        ].map((s) => (
          <div key={s.k} className="inline-flex items-baseline gap-1.5 rounded-lg border border-zinc-800 bg-zinc-900/50 px-3 py-1.5">
            <span className={`text-base font-bold tabular-nums ${s.c}`}>{s.v}</span>
            <span className="text-[11px] uppercase tracking-wide text-zinc-500">{s.k}</span>
          </div>
        ))}
        <div className="ml-auto inline-flex items-center gap-1.5 text-[11px] text-zinc-500" title="Updates automatically every 45s">
          <Radio className="h-3.5 w-3.5 animate-pulse text-emerald-400" />
          <span suppressHydrationWarning>Live · updated {ago(updatedAt, now) || 'just now'}</span>
        </div>
      </div>

      {/* Tabs + time-window toggle */}
      <div className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-2">
        <div className="flex flex-wrap gap-1.5">
          {TABS.map((t) => {
            const Icon = t.icon
            const on = tab === t.id
            return (
              <button key={t.id} onClick={() => setTab(t.id)} title={t.hint}
                className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-semibold transition-all ${
                  on ? 'border-amber-500/50 bg-amber-500/15 text-amber-300' : 'border-zinc-800 bg-zinc-900/40 text-zinc-400 hover:border-zinc-700 hover:text-zinc-200'
                }`}>
                <Icon className="h-4 w-4" />{t.label}
              </button>
            )
          })}
        </div>
        {/* Day (whole session) vs Last 2h (live intraday, like Stock Market Today) */}
        <div className="ml-auto inline-flex rounded-lg border border-zinc-800 bg-zinc-900/40 p-0.5 text-xs font-semibold">
          {([['day', 'Today'], ['recent', 'Last 2h']] as const).map(([w, label]) => (
            <button key={w} onClick={() => setWin(w)}
              title={w === 'day' ? "The whole trading session" : "Only what's alerted in the last 2 hours"}
              className={`rounded-md px-2.5 py-1 transition-colors ${
                win === w ? 'bg-amber-500/15 text-amber-300' : 'text-zinc-400 hover:text-zinc-200'
              }`}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Shared universe filter — same control on every stock list. Leveraged/inverse ETFs are one of its
          exclusions now (on by default), so the old standalone toggle is gone. */}
      <ListFilters value={filters} onChange={setFilters} hiddenCount={hiddenByFilter} className="mb-3" />

      {sorted.length === 0 ? (
        <div className="rounded-xl border border-zinc-800 bg-zinc-900/50 p-10 text-center">
          <Flame className="mx-auto mb-3 h-8 w-8 text-zinc-700" />
          <p className="text-sm text-zinc-400">Feeds populate as scanner alerts fire.</p>
          <p className="mt-1 text-xs text-zinc-600">Check back during market hours - the board refreshes itself.</p>
        </div>
      ) : (
        <ul className="space-y-2">
          {sorted.map((r, i) => {
            const pct = pctOf(r)
            const price = priceOf(r)
            const rb = rankBadge(i)
            const hot = isHot(r)
            return (
              <li key={r.symbol}>
                <Link href={`/stocks/${encodeURIComponent(r.symbol)}`}
                  className="group flex items-center gap-3 rounded-xl border border-zinc-800 bg-zinc-900/40 px-3 py-3 transition-all hover:border-amber-600/40 hover:bg-zinc-900/70 sm:gap-4 sm:px-4">
                  <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-xs font-bold tabular-nums ${i < 3 ? `ring-1 ${rb.cls}` : rb.cls}`}>{rb.label}</span>
                  <Logo symbol={r.symbol} logo={r.logo} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-zinc-100 group-hover:text-amber-300">${r.symbol}</span>
                      {hot && (
                        <span className="inline-flex items-center gap-0.5 rounded bg-rose-500/15 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-rose-300">
                          <Flame className="h-2.5 w-2.5" />Hot
                        </span>
                      )}
                      {r.runner && r.runner.days >= 2 && (() => {
                        const fading = runnerRecency(r.runner.sessionsAgo) === 'fading'
                        return (
                          <span
                            title={fading
                              ? `Multi-day runner cooling off - Top-6 on ${r.runner.days} of the last 10 sessions, but not in the last ${r.runner.sessionsAgo}`
                              : `Held a Top-6 slot on ${r.runner.days} of the last 10 sessions${r.runner.bestRank ? `, best rank #${r.runner.bestRank}` : ''}`}
                            className={`inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[9px] font-bold ring-1 ${fading ? 'bg-zinc-700/20 text-zinc-400 ring-zinc-600/40' : 'bg-orange-500/15 text-orange-300 ring-orange-500/30'}`}
                          >
                            {fading ? <TrendingDown className="h-2.5 w-2.5" /> : <Flame className="h-2.5 w-2.5" />}{r.runner.days}d runner
                          </span>
                        )
                      })()}
                      {isLevEtf(r) && (
                        <span
                          className="inline-flex cursor-help items-center gap-0.5 rounded bg-violet-500/15 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-violet-300 ring-1 ring-violet-500/25"
                          title={LEV_TOOLTIP}
                          onClick={(e) => { e.preventDefault(); e.stopPropagation() }}
                        >
                          <Zap className="h-2.5 w-2.5" />2×/3×
                        </span>
                      )}
                    </div>
                    <div className="truncate text-xs text-zinc-500">{r.name || 'View feed'}</div>
                    {(() => { const m = uMeta.get(r.symbol.toUpperCase()); return m ? <div className="mt-1"><UniverseChips marketCap={m.marketCap} country={m.country} /></div> : null })()}
                  </div>

                  <div className="hidden shrink-0 sm:block"><Spark data={r.spark} /></div>

                  <div className="w-[4.5rem] shrink-0 text-right sm:w-20">
                    <div className="font-mono text-sm text-zinc-200">{price != null ? `$${fmtPrice(price)}` : '—'}</div>
                    <div className={`font-mono text-xs font-semibold ${pct == null ? 'text-zinc-600' : pct >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                      {pct == null ? '—' : `${pct >= 0 ? '+' : ''}${pct.toFixed(2)}%`}
                    </div>
                  </div>

                  <div className="hidden w-28 shrink-0 items-center justify-end gap-3 md:flex">
                    <span className="inline-flex items-center gap-1 text-sm text-amber-400/90" title={`${r.alerts} alerts in 24h`}>
                      <Flame className="h-3.5 w-3.5" /><span className="tabular-nums">{r.alerts}</span>
                    </span>
                    <span className="inline-flex items-center gap-1 text-sm text-sky-400/90" title={`${r.posts} community posts`}>
                      <MessageCircle className="h-3.5 w-3.5" /><span className="tabular-nums">{r.posts}</span>
                    </span>
                  </div>

                  <div className="hidden w-20 shrink-0 text-right text-[11px] text-zinc-600 lg:block" suppressHydrationWarning title="Most recent alert">
                    {ago(r.lastAlertMs, now)}
                  </div>

                  <ArrowUpRight className="h-4 w-4 shrink-0 text-zinc-700 transition-colors group-hover:text-amber-400" />
                </Link>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
